import React from 'react';
import { useCallback } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getArticle, formatFileType } from '../utils/stringUtils';
import { useFeedbackContext } from '../context';
import { serviceLogger } from '../utils/serviceLogger';

/**
 * Helper function to extract plain text from a React element.
 * This function recursively concatenates the text from the children.
 */
const extractTextFromReactElement = (element) => {
  if (typeof element === 'string') return element;
  if (typeof element === 'number') return element.toString();
  if (React.isValidElement(element)) {
    const { children } = element.props;
    return React.Children.toArray(children)
      .map((child) => extractTextFromReactElement(child))
      .join(' ');
  }
  return '';
};

/**
 * Custom hook to handle data export functionality for CSV and Excel formats.
 * Automatically triggers a toast notification on success or failure.
 *
 * @param {Array} data - The dataset to be exported.
 * @param {Array} columns - The column definitions, where each object includes:
 *   - {String} header - The display name of the column (used in most views).
 *   - {String} field - The key to access the corresponding value in the data array.
 *   - {String} label - (Optional) Used for views with a `key-label` structure (e.g., Location History).
 *   - {String} key - (Optional) The key used to match columns in specific views.
 * @param {String} title - The title used for the exported file name.
 *
 * @returns {Function} exportData - A function that executes the export logic based on the selected format.
 * @param {String} format - The export format ('csv' or 'excel').
 */
const useExportData = (data, columns, title) => {
  const { showToast } = useFeedbackContext();

  const exportData = useCallback(
    (format) => {
      try {
        const exportRows = data.map((row) => {
          let rowData = {};

          if (row.type === 'start') {
            // Handle "start" rows
            rowData = { [columns[0]?.label || 'Session Info']: row.data.text };
          } else if (row.data?.columns && Array.isArray(row.data.columns)) {
            // Handle rows that have a columns array (applies to both "normal" and "indented")
            row.data.columns.forEach((col) => {
              const matchingColumn = columns.find((c) => c.key === col.id);
              if (matchingColumn) {
                let rawValue = col.value;
                if (
                  React.isValidElement(rawValue) &&
                  rawValue.props &&
                  typeof rawValue.props.text === 'string'
                ) {
                  rawValue = rawValue.props.text;
                }
                rowData[matchingColumn.label] = rawValue;
              }
            });
          } else if (row.type === 'end') {
            // Handle "end" rows.
            let exportedText = '';
            if (row.data.content) {
              exportedText = extractTextFromReactElement(row.data.content);
            } else {
              let durationValue = row.data.totalDuration;
              if (React.isValidElement(durationValue)) {
                if (durationValue.props && typeof durationValue.props.text === 'string') {
                  durationValue = durationValue.props.text;
                } else {
                  durationValue = extractTextFromReactElement(durationValue);
                }
              }
              exportedText = (row.data.text ? row.data.text + ' ' : '') + (durationValue || '');
            }
            rowData = { [columns[0]?.label || 'Export']: exportedText };
          } else {
            // Fallback for views using `header` & `field` structure
            columns.forEach((col) => {
              rowData[col.header] = row[col.field] ?? '';
            });
          }

          return rowData;
        });

        if (format === 'csv') {
          const worksheet = XLSX.utils.json_to_sheet(exportRows);
          const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
          const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
          saveAs(blob, `${title}.csv`);
        } else if (format === 'excel') {
          const worksheet = XLSX.utils.json_to_sheet(exportRows);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
          });
          saveAs(blob, `${title}.xlsx`);
        }

        const article = getArticle(format);
        const formattedFormat = formatFileType(format);
        showToast({
          variant: 'success',
          title: 'Export successful',
          copy: `Your data has been successfully exported as ${article} ${formattedFormat} file.`,
        });
      } catch (error) {
        serviceLogger.error('Export error:', error);
        showToast({
          variant: 'error',
          title: 'Export failed',
          copy: 'We were unable to export your data. Please try again.',
        });
      }
    },
    [data, columns, title, showToast],
  );

  return exportData;
};

export default useExportData;
